// #212 PR-B — widget-side identity store + OnCatch.identify/reset API.
//
// Pattern: queue-then-flush. The widget script is loaded async+defer, so
// host pages may call OnCatch.identify(...) BEFORE the script has parsed.
// The install snippet (documented in packages/widget/README.md) publishes
// a stub window.OnCatch synchronously that queues calls into _q. When
// this module runs (after bootstrap), it replaces the stub with the real
// implementation and drains _q in FIFO order.
//
// Module-scoped state: current identity + the workspace's
// identification_enabled flag (loaded by bootstrap from widget-config).
// Validation goes through the shared Zod-free validator so the widget
// bundle stays under the 80 KB cap.

import type { IdentifiedReporter } from '@oncatch/types/identified-reporter-validator';
import { validateIdentifiedReporter } from '@oncatch/types/identified-reporter-validator';

// =============================================================================
// Module-scoped state
// =============================================================================

let currentIdentity: IdentifiedReporter | null = null;
let workspaceIdentificationEnabled = false;
let workspaceSensitiveDataMode = false;

// =============================================================================
// Stub shape (what the install snippet publishes)
// =============================================================================

interface OnCatchStub {
  _q?: Array<[string, unknown?]>;
  identify?: (args: unknown) => void;
  reset?: () => void;
}

// =============================================================================
// Public read API for ui.ts + bootstrap.ts
// =============================================================================

export function getCurrentIdentity(): IdentifiedReporter | null {
  return currentIdentity;
}

export function isIdentificationActive(): boolean {
  return workspaceIdentificationEnabled && !workspaceSensitiveDataMode && currentIdentity !== null;
}

export function setWorkspaceFlags(flags: {
  identificationEnabled: boolean;
  sensitiveDataMode: boolean;
}): void {
  workspaceIdentificationEnabled = flags.identificationEnabled;
  workspaceSensitiveDataMode = flags.sensitiveDataMode;
}

// =============================================================================
// Real implementations of identify() and reset() — published by
// installRealApi() after bootstrap
// =============================================================================

function realIdentify(args: unknown): void {
  const result = validateIdentifiedReporter(args);
  if (!result.ok) {
    const summary = result.errors
      .slice(0, 3)
      .map((e) => `${e.path || '(root)'}: ${e.reason}`)
      .join(', ');
    throw new TypeError(`OnCatch.identify rejected: ${summary}`);
  }
  currentIdentity = result.data;
}

function realReset(): void {
  currentIdentity = null;
}

// =============================================================================
// Bootstrap entry — replace stub with real API + drain queue
// =============================================================================

const QUEUE_FLUSH_CAP = 100;

export function installRealApi(): void {
  const w = globalThis as unknown as { OnCatch?: OnCatchStub };
  const stub = w.OnCatch;
  const existingQueue: Array<[string, unknown?]> = Array.isArray(stub?._q) ? stub._q : [];

  // Drain queue with per-call try/catch so one bad call doesn't break the rest.
  // Cap at QUEUE_FLUSH_CAP to bound the work even if a host page spammed calls.
  const toFlush = existingQueue.slice(0, QUEUE_FLUSH_CAP);
  if (existingQueue.length > QUEUE_FLUSH_CAP) {
    // biome-ignore lint/suspicious/noConsole: bounded diagnostic on overflow
    console.warn(
      `[OnCatch] queue had ${existingQueue.length} entries; flushing first ${QUEUE_FLUSH_CAP}.`,
    );
  }
  for (const entry of toFlush) {
    try {
      const [method, args] = entry;
      if (method === 'identify') realIdentify(args);
      else if (method === 'reset') realReset();
    } catch (err) {
      // biome-ignore lint/suspicious/noConsole: surface flush-time validation failures
      console.warn(`[OnCatch] flushing queued call failed: ${(err as Error).message ?? err}`);
    }
  }

  // Replace stub with real API. Subsequent post-bootstrap calls execute
  // synchronously; bad inputs throw TypeError (familiar JS API behavior).
  w.OnCatch = {
    identify: realIdentify,
    reset: realReset,
  };
}
